/* ══════════════════════════════════════════════════════════════════════════
 * EVERY RESTAURANT TAKES ITS TURN AT THE FRONT
 *
 * This is `Eleater Prototype/eleat_rotate.js` ported to React. It is not a new
 * system and not an improvement: the rule, the storage key, the turn hours and
 * the seed are all copied, so the prototype and the app cannot disagree about
 * who is in slot one.
 *
 * Al, 7 September 2026: *"so 1 goes to 2 then 3 and 4 and 5 etc. all the way
 * to 20. so the each one also gets its place at 1 so number 20 gets slot 1 in
 * the entire run do you understand this"*
 *
 * ⚠️ THIS IS A FAIRNESS MECHANISM, NOT AN ANIMATION. It is the visible half of
 * the Premium Placement promise: one flat price buys ENTRY to the carousel,
 * never a POSITION in it. A venue that is always twentieth has bought nothing.
 *
 * ⚠️ ROTATION, NOT A SHUFFLE, and the prototype says why: *"A shuffle is fair
 * ON AVERAGE and guarantees nothing: with twenty venues, one can go a hundred
 * loads without ever being first, and the restaurant paying the same flat fee
 * as everyone else has no way to know."*
 *
 * ── ⚠️ NONE OF THIS EXISTED IN REACT — 23 September 2026 ────────────────
 *
 * Al: *"the carousel doesn't work now. So it's not carouseling. None of the
 * daily deals, last minute specials, happy hours are carouseling."*
 *
 * `eleat_rotate.js` lives only in `Eleater Prototype/`. `Home.jsx`,
 * `Explore.jsx` and `ViewAll.jsx` have never called it, so every list in the
 * React app has been drawn in whatever order the server returned, every time,
 * for every Eleater. **The fairness promise was not weakened; it was absent.**
 *
 * ⚠️ AND "CAROUSEL" MEANS TWO DIFFERENT THINGS, which is why this is two
 * files. This one owns the ORDER — who is in slot one. `Carousel.jsx` owns the
 * ARROWS and the DRIFT — the `‹ ›` buttons and the slow auto-scroll. The
 * prototype splits them the same way and says so: *"Neither touches the
 * other's job: one scrolls a rail, the other arranges it before anybody
 * scrolls anything."*
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE PROTOTYPE'S OWN STORAGE KEY AND FIELD. An Eleater who used the
 * prototype and then the app keeps their place in the rotation; two keys would
 * be two rotations, and they would disagree about whose turn it is. */
const KEY = 'eleat_v1';
const FIELD = 'railTurn';

function readTurns() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || '{}');
    return (s && s[FIELD]) || {};
  } catch { return {}; }
}

function writeTurn(id, n) {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || '{}');
    s[FIELD] = s[FIELD] || {};
    s[FIELD][id] = n;
    localStorage.setItem(KEY, JSON.stringify(s));
    return true;
  } catch { return false; }
  /* ⚠️ IT DEGRADES RATHER THAN THROWING. Private browsing refuses storage; with
   * none the rail simply does not rotate, which is the honest outcome. */
}

/* ⚠️⚠️ ONE TURN PER RAIL PER PAGE LOAD, HOWEVER OFTEN WE ARE ASKED.
 * React re-renders. Without this, every render would advance the counter and a
 * venue would silently lose its turn at the front — precisely the unfairness
 * this exists to prevent. The prototype has the same guard for the same
 * reason; in React it matters more, not less. */
const TAKEN = {};

/* ⚠️⚠️ EVERY HOUR, 08:00 THROUGH 23:00 — CHANGED 24 SEPTEMBER 2026.
 *
 * It was four: 08:00 · 12:00 · 16:00 · 20:00, from Al on 8 September — *"we
 * just keep it from eight AM till midnight and keep those hours. So sixteen
 * hours, I guess, that it changes every four hours."*  The WAKING WINDOW is
 * unchanged and is still his: eight in the morning until midnight, and nothing
 * overnight. Only how often it turns inside that window has moved.
 *
 * Al, 24 September 2026: *"we're not looking at 50 restaurants. We're going to
 * look at 500 restaurants, 1,000 restaurants. What are we going to do then?"*
 *
 * ⚠️ HE IS RIGHT, AND HE IS RIGHT ABOUT SOMETHING THE CLOCK CANNOT FIX.
 * The rule rotates the list left by ONE each turn, so a venue in slot N needs
 * N-1 turns to lead. A full cycle is therefore as long as the list:
 *
 *      50 venues   @ 4h = 8 days     @ 1h = 2 days
 *   1,000 venues   @ 4h = 166 days   @ 1h = 42 days
 *
 * Halving the clock buys 2×. Going from 50 venues to 1,000 costs 20×. So an
 * hourly turn is the right answer for the next year and is NOT the answer at a
 * thousand venues — and it was said out loud rather than quietly assumed.
 *
 * ⚠️ WHAT ACTUALLY SAVES IT AT SCALE IS THAT THE LIST IS FILTERED. Al: *"not
 * all thousand restaurants are going to be in one location. They're going to
 * be in different locations, Wan Chai, TST, Admiralty, Soho."*  Explore and
 * every View All scope by area, cuisine and deal type, so the list a person
 * actually sees is rarely the whole market. Revisit when ONE market passes
 * roughly 150 venues in a single view.
 *
 * ⚠️ AND NOT HALF-HOURLY. Explore is a list somebody scrolls and searches;
 * reshuffling it twice an hour means a person who looks before and after lunch
 * finds it rearranged under them. Home already turns on EVERY page load, so
 * the shop window is doing the heavy lifting regardless.
 *
 * ⚠️ MIDNIGHT STILL MUST NOT COUNT AS A CHANGE — anything before 08:00 belongs
 * to the previous evening's last turn, exactly as before. */
const TURNS_AT = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

/* ⚠️⚠️ THE DAY AND THE HOUR MUST COME FROM THE SAME CLOCK. The prototype
 * counted days off the world clock and hours off the phone, and an audit found
 * that in Asia/Hong_Kong it turned at MIDNIGHT as well — the one hour Al said
 * to leave alone — and in America/Edmonton it also turned at 18:00, the middle
 * of the dinner rush. Everything below is read from the LOCAL date. */
function slotNow() {
  let d;
  try { d = new Date(); } catch { return 0; }
  const y = d.getFullYear(), m = d.getMonth(), day = d.getDate(), h = d.getHours();
  const local = Date.UTC(y, m, day) / 86400000;
  if (h < TURNS_AT[0]) return (local - 1) * TURNS_AT.length + (TURNS_AT.length - 1);
  let k = 0;
  for (let i = 0; i < TURNS_AT.length; i++) if (h >= TURNS_AT[i]) k = i;
  return local * TURNS_AT.length + k;
}

/* ⚠️⚠️ AND IT IS STILL DIFFERENT FOR EVERY ELEATER. Al, 7 September: *"everyone
 * sees something different because the carousel is moving differently for every
 * eleater."* A clock alone would give every phone in the market the same leader
 * at the same moment — one venue owning the front for all of them, which is the
 * opposite of the rule. Each Eleater gets a starting point, made once and kept;
 * the clock walks everyone on from where they started. */
function seed() {
  try {
    const st = JSON.parse(localStorage.getItem(KEY) || '{}');
    if (typeof st.railSeed === 'number') return st.railSeed;
    const n = Math.floor(Math.random() * 100000);
    st.railSeed = n;
    localStorage.setItem(KEY, JSON.stringify(st));
    return n;
  } catch { return 0; }
}

/* ⚠️ THE LIST'S OWN NAME GOES INTO THE SUM, or every list turns together.
 * Without it the five View All screens and Explore all landed on the same
 * restaurant at the same moment — one rotation wearing six hats. */
function nameOffset(id) {
  let h = 0;
  const str = String(id || '');
  for (let i = 0; i < str.length; i++) h = ((h * 31) + str.charCodeAt(i)) % 100000;
  return h;
}

function heldTurn(id, n) {
  if (n < 2) return 0;
  /* the hour is in the key — held for the hour, never for the whole time
     the app stays open (audit, 7 Oct 2026: it turned only on a reload) */
  const k = id + '|held|' + slotNow();
  if (Object.prototype.hasOwnProperty.call(TAKEN, k)) return TAKEN[k] % n;
  let t = (seed() + nameOffset(id) + slotNow()) % n;
  if (t < 0) t += n;
  TAKEN[k] = t;
  return t;
}

function turnFor(id, n) {
  if (n < 2) return 0;
  if (Object.prototype.hasOwnProperty.call(TAKEN, id)) return TAKEN[id] % n;
  const turns = readTurns();
  let turn = parseInt(turns[id], 10);
  if (!(turn >= 0)) turn = 0;
  /* ⚠️ ADVANCE FIRST, THEN DRAW. Drawing at the stored value and advancing
   * afterwards shows the same leader twice — once on the load that wrote it
   * and once on the load that reads it. */
  turn = (turn + 1) % n;
  writeTurn(id, turn);
  TAKEN[id] = turn;
  return turn;
}

function spin(list, turn) {
  if (!turn) return list.slice();
  return list.slice(turn).concat(list.slice(0, turn));
}

/**
 * A HOME RAIL — advances on EVERY page load.
 *
 * ⚠️ AL CORRECTED ME ON THIS AND THE CORRECTION IS THE WHOLE POINT:
 * *"It's only explore that has a four hour turnaround. All the other ones are
 * turning and turning and turning."*  Home is a shop window nobody is trying
 * to find their way back to.
 */
export function rail(id, list) {
  if (!list || list.length < 2) return list || [];
  return spin(list, turnFor(String(id || 'rail'), list.length));
}

/**
 * EXPLORE AND THE FIVE VIEW ALLS — held on the four-hour clock.
 *
 * ⚠️ A LIST THAT MOVES EVERY TIME YOU OPEN IT IS A LIST YOU CANNOT COME BACK
 * TO. Al, 8 September 2026, choosing between rotating every load and rotating
 * on a clock: *"go with b."*  Somebody who finds a place at lunchtime has to
 * be able to find it again at six.
 *
 * ⚠️ IT NEVER SORTS AND IT NEVER FILTERS. If an Eleater has asked for Nearest
 * or Top rated, that order is their answer and rotation must not touch it.
 */
export function held(id, list) {
  if (!list || list.length < 2) return list || [];
  return spin(list, heldTurn(String(id || 'list'), list.length));
}

/* A NEW VISIT TO HOME — taps switch screens in place (nav.js), so Home is
   drawn again without a page load and TAKEN still held the last turn: Home
   did not turn when you came back to it (audit, 7 Oct 2026). Home calls this
   once when it opens; the guard against turning twice in one drawing stays. */
export function newVisit(prefix) {
  for (const k of Object.keys(TAKEN)) if (k.startsWith(prefix) && !k.includes('|held')) delete TAKEN[k];
}

/* ⚠️ EXPORTED FOR THE GATE ONLY — so a check can prove the turn advances, and
 * prove the held turn does NOT, without reaching into localStorage itself. */
export const _internals = { turnFor, heldTurn, slotNow, TURNS_AT, TAKEN };
